<template>
  <div class="min-h-screen bg-[#FAFAFA] antialiased">
    <!-- 主内容区域 -->
    <main class="min-h-screen">
      <!-- 顶部分类区域 -->
      <div class="sticky top-0 z-40 bg-[#FAFAFA]/80 backdrop-blur-md border-b border-gray-100">
        <div class="max-w-7xl mx-auto px-6 py-4">
          <!-- 分类标签 -->
          <div class="flex items-center gap-2 mt-4 overflow-x-auto pb-1 scrollbar-hide">
            <div v-if="loadingCategories" class="text-gray-400 text-sm">加载分类中...</div>
            <template v-else>
              <button
                v-for="cat in categories"
                :key="cat.id"
                @click="selectCategory(cat)"
                :class="[
                  'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200',
                  selectedCategory?.id === cat.id
                    ? 'bg-[#7AB3F5] text-white shadow-md shadow-[#7AB3F5]/25'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#7AB3F5] hover:text-[#7AB3F5]'
                ]"
              >
                {{ cat.name }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- 壁纸内容区域 -->
      <div class="max-w-7xl mx-auto px-6 py-6">
        <!-- 加载状态 -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
          <div class="w-10 h-10 border-3 border-[#7AB3F5]/20 border-t-[#7AB3F5] rounded-full animate-spin"></div>
          <p class="text-gray-400 mt-4 text-sm">加载中...</p>
        </div>
        
        <!-- 错误状态 -->
        <div v-else-if="error" class="flex flex-col items-center justify-center py-20 text-red-500">
          <svg class="w-12 h-12 mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p>{{ error }}</p>
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="images.length === 0" class="flex flex-col items-center justify-center py-20 text-gray-400">
          <svg class="w-16 h-16 mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <p class="text-lg">暂无壁纸</p>
          <p class="text-sm mt-1 opacity-60">试试其他分类</p>
        </div>

        <!-- 壁纸网格 -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          <div
            v-for="image in images"
            :key="image.id"
            class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
            @click="openImage(image)"
          >
            <div class="aspect-[4/3] bg-gray-100 relative overflow-hidden">
              <img
                :src="image.url"
                :alt="image.utag || '壁纸'"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                @error="handleImageError"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div class="p-4">
              <h3 class="font-medium text-gray-800 truncate text-sm">{{ image.utag || '未命名' }}</h3>
              <div class="flex items-center justify-between mt-2">
                <span class="text-xs text-gray-400">{{ image.category || selectedCategory?.name || '壁纸' }}</span>
                <span v-if="image.resolution" class="text-xs text-gray-300">{{ image.resolution }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 加载更多指示器 -->
        <div v-if="!loading && hasMore" ref="loadMoreTrigger" class="flex justify-center mt-10 py-4">
          <div class="w-6 h-6 border-2 border-[#7AB3F5]/20 border-t-[#7AB3F5] rounded-full animate-spin"></div>
        </div>
        <div v-if="!hasMore && images.length > 0" class="text-center text-gray-400 text-sm mt-8 pb-4">
          已加载全部壁纸
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-white border-t border-gray-100 py-6">
      <div class="max-w-7xl mx-auto px-6 text-center text-gray-500 text-sm">
        <a href="https://www.160621.xyz" target="_blank" rel="noopener noreferrer" class="hover:text-[#7AB3F5] transition-colors">
          © 2021-{{ currentYear }} 魔法代码
        </a>
      </div>
    </footer>

    <!-- 图片预览弹窗 -->
    <Transition name="fade">
      <div
        v-if="selectedImage"
        class="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
        @click.self="closeImage"
      >
        <div class="max-w-6xl max-h-[95vh] relative flex flex-col items-center">
          <img
            :src="selectedImage.url.replace('/r/', '/')"
            :alt="selectedImage.utag || '壁纸'"
            class="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl"
          />
          <div class="text-white text-center mt-6">
            <h3 class="text-xl font-medium">{{ selectedImage.utag || '未命名' }}</h3>
            <p class="text-gray-400 mt-2 text-sm">{{ selectedImage.desc || '' }}</p>
            <p class="text-gray-500 text-xs mt-3">分辨率: {{ selectedImage.resolution }}</p>
          </div>
          <button
            @click="closeImage"
            class="absolute -top-2 -right-2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full text-white flex items-center justify-center transition-all duration-200"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const categories = ref([])
const selectedCategory = ref(null)
const images = ref([])
const loading = ref(false)
const loadingCategories = ref(false)
const loadingMore = ref(false)
const error = ref('')
const selectedImage = ref(null)
const start = ref(0)
const hasMore = ref(true)
const loadMoreTrigger = ref(null)
let observer = null

// 当前年份
const currentYear = new Date().getFullYear()

// API 基础URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// 获取分类列表
const fetchCategories = async () => {
  loadingCategories.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/img/360/categories`)
    const data = await response.json()
    
    if (data.errno === '0' && data.data) {
      categories.value = data.data
      // 默认选中第一个分类
      if (categories.value.length > 0) {
        selectedCategory.value = categories.value[0]
        await fetchWallpapers(categories.value[0].id)
      }
    }
  } catch (err) {
    console.error('获取分类失败:', err)
    error.value = '获取分类失败，请刷新重试'
  } finally {
    loadingCategories.value = false
  }
}

// 获取壁纸列表
const fetchWallpapers = async (cid, append = false) => {
  if (!append) {
    loading.value = true
    start.value = 0
    images.value = []
  } else {
    loadingMore.value = true
  }
  error.value = ''
  
  try {
    const response = await fetch(`${API_BASE_URL}/img/360/wallpapers?cid=${cid}&start=${start.value}`)
    const data = await response.json()
    
    if (data.errno === '0' && data.data) {
      const newImages = data.data.map(item => ({
        id: item.id,
        url: item.url,
        utag: item.utag,
        desc: item.desc,
        resolution: item.resolution,
        category: selectedCategory.value?.name
      }))
      
      if (append) {
        images.value.push(...newImages)
      } else {
        images.value = newImages
      }
      
      // 如果没有返回数据或数据少于16条，说明没有更多了
      hasMore.value = newImages.length === 16
    } else {
      hasMore.value = false
    }
  } catch (err) {
    console.error('获取壁纸失败:', err)
    error.value = '获取壁纸失败，请刷新重试'
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// 选择分类
const selectCategory = async (cat) => {
  if (selectedCategory.value?.id === cat.id) return
  selectedCategory.value = cat
  await fetchWallpapers(cat.id)
}

// 打开图片预览
const openImage = (image) => {
  selectedImage.value = image
}

// 关闭图片预览
const closeImage = () => {
  selectedImage.value = null
}

// 加载更多
const loadMore = async () => {
  if (!selectedCategory.value || loadingMore.value) return
  start.value += 16
  await fetchWallpapers(selectedCategory.value.id, true)
}

// 图片加载失败处理
const handleImageError = (e) => {
  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="225"%3E%3Crect width="400" height="225" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af"%3E图片加载失败%3C/text%3E%3C/svg%3E'
}

// 设置IntersectionObserver
const setupIntersectionObserver = () => {
  if (observer) {
    observer.disconnect()
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && hasMore.value && !loadingMore.value) {
        loadMore()
      }
    })
  }, {
    rootMargin: '100px',
    threshold: 0.1
  })

  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value)
  }
}

// 监听loadMoreTrigger变化
watch([loadMoreTrigger, hasMore], () => {
  if (loadMoreTrigger.value && hasMore.value) {
    setupIntersectionObserver()
  }
}, { immediate: false })

onMounted(() => {
  fetchCategories()
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
/* 隐藏滚动条 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* 淡入淡出过渡 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 边框宽度 */
.border-3 {
  border-width: 3px;
}
</style>
